/**
 * DPL DEW — Quotation & Estimate Types
 *
 * DESIGN PRINCIPLE: Catalogue is the single source of truth.
 * - EstimateType, ScopeGroup (formerly "RoomCategory"), and SectionWorkType
 *   are all driven by the catalogue's own category/tag taxonomy.
 * - No interior-specific values are hardcoded here; all domain
 *   flavour comes from the settings and catalogue records.
 */

import type { MeasurementUnit } from './measurements';
import type { PricingRules } from '@/utils/pricingEngine';

// ─── Status ──────────────────────────────────────────────────────────────────

export type QuoteStatus =
  | 'draft'
  | 'revised'
  | 'finalized'
  | 'pending_approval'
  | 'rejected'
  // Immutable lifecycle states (set by DB RPCs, never by the client directly)
  | 'superseded'   // replaced by a newer revision in the same group
  | 'cancelled'    // administratively cancelled (commercial fields frozen)
  | 'archived';    // soft-archived, permanently read-only

/** Whether a quotation version is permanently immutable (no commercial edits allowed) */
export const isImmutableStatus = (status: QuoteStatus | string): boolean =>
  ['finalized', 'superseded', 'cancelled', 'archived'].includes(status);

/** Whether a quotation is the active/latest record for its group */
export const isActiveStatus = (status: QuoteStatus | string): boolean =>
  ['draft', 'pending_approval', 'finalized'].includes(status);

/** The origin type of a quotation row in the lineage tree */
export type RevisionType =
  | 'original'   // first version in a new group
  | 'revision'   // commercial revision of a finalized quote
  | 'duplicate'  // independent copy with fresh group_id
  | 'imported'
  | 'restored';

// ─── Estimate type ────────────────────────────────────────────────────────────
// Free-form string so any industry can define their own types via settings.
// The UI builds <Select> options from AppPreferences.estimate_types.
export type EstimateType = string;

// ─── Unit of measure ─────────────────────────────────────────────────────────
// Open string — canonical values come from catalogue items.
export type UnitOfMeasure = string;
export const STANDARD_UOMS = [
  'Nos', 'Sqft', 'Rft', 'Set', 'Lot', 'Mtr', 'Pcs', 'Unit', 'Sqm', 'Rm', 'Hr', 'Day', 'Kg', 'Ltr'
] as const;

// ─── Scope groups (formerly "Room") ──────────────────────────────────────────
// A "scope group" is any logical container for line items.
// For interiors it's a room; for IT it's a module; for events it's a zone.
// The TYPE is open string — valid values come from AppPreferences.scope_group_types.
export type ScopeGroupType = string;

export interface ScopeGroupDefinition {
  value: ScopeGroupType;
  label: string;
  allowMultiple: boolean;  // e.g. "Bedroom" yes; "Living Room" no
  group?: string;          // UI grouping label
}

// Kept for backward compat with existing DB rows
export type RoomCategory = ScopeGroupType;
export interface RoomSelection { category: ScopeGroupType; count: number; }
export interface RoomInstance {
  id: string;
  category: ScopeGroupType;
  instanceNumber: number;
  displayName: string;
  customName?: string;
}

// ─── Company & Client ─────────────────────────────────────────────────────────

export interface CompanyInfo {
  brandName: string;
  legalName: string;
  gstin?: string;
  address: {
    line1: string;
    line2?: string;
    line3?: string;
    city: string;
    state: string;
    country: string;
    pincode: string;
  };
  contact: {
    bdmId: string;
    bdmName: string;
    phone: string;
    email: string;
  };
  logoUrl?: string;
  authorizedSignatory?: string;
}

export interface BdmInfo {
  id: string;
  name: string;
  phone?: string;
  email?: string;
}

export interface ClientInfo {
  name: string;
  projectName?: string;
  projectAddress?: string;
  projectType?: string;
  phone?: string;
  email?: string;
  projectRoomCounts?: Record<string, number>;
}

// ─── Line items ───────────────────────────────────────────────────────────────

export interface LineItemDimension {
  width?: number;
  height?: number;
  depth?: number;
  unit: MeasurementUnit;
  sqft?: number;
}

export interface LineItemSubItem {
  id: string;
  label: string;
  description?: string;
  quantity: number;
  uom: UnitOfMeasure;
  dimensions?: LineItemDimension;
  rate?: number;
  isLumpsum?: boolean;
  subTotal: number;
  pricingRules?: PricingRules;
}

export interface LineItem {
  id: string;
  slNo: number;
  description: string;
  specifications?: string;
  quantity: number;
  uom: UnitOfMeasure;
  rate: number;
  total: number;
  dimensions?: LineItemDimension;
  subItems?: LineItemSubItem[];
  // Catalogue link — the item's category tag drives filtering/display
  catalog_item_id?: string;
  catalogCategory?: string;   // snapshot from catalogue at time of addition
  snapshot_tags?: string[];   // hierarchical tags snapshot
  brandId?: string;
  materialGrade?: string;
  sort_order?: number;
  pricingRules?: PricingRules;
}

// ─── Sections ─────────────────────────────────────────────────────────────────
// workType is now a free string (catalogue-driven).
// Built-in fallbacks: 'standard' | 'custom'
// Industry-specific values ('modular','civil','blinds') should live in settings.

export interface ScopeGroup {           // was: RoomItemGroup
  scopeId: string;                      // was: roomId
  scopeName: string;                    // was: roomName
  items: LineItem[];
  subtotal: number;
}

// Backward compat aliases
export type RoomItemGroup = ScopeGroup;

export interface QuotationSection {
  id: string;
  name: string;
  workType: string;                     // open string; comes from catalogue taxonomy
  roomGroups: ScopeGroup[];
  sectionSubtotal: number;
  gstEnabled: boolean;
  taxNote?: string;
}

// ─── Financials ───────────────────────────────────────────────────────────────

export interface FinancialProvenance {
  pricingEngineVersion: string;
  calculationTimestamp: string;
  currency: string;
  gstMode: string;
  roundingStrategy: string;
}

export interface Financials {
  subtotal: number;
  discountType?: 'flat' | 'percentage';
  discountValue?: number;
  discountAmount?: number;
  taxName: string;
  taxRate: number;
  taxAmount: number;
  roundingAdjustment?: number;
  grandTotal: number;
  paymentScheduleAuto?: boolean;
  provenance?: FinancialProvenance;
}

// Payment milestones — structure is generic; labels & percentages come from settings
export interface PaymentMilestone {
  id: string;
  milestoneName: string;
  percentage: number;
  amount: number;
  dueStage?: string;
}

// Default milestones — overridden by AppPreferences.default_payment_schedule
export const DEFAULT_PAYMENT_MILESTONES: Omit<PaymentMilestone, 'id' | 'amount'>[] = [
  { milestoneName: 'Advance',    percentage: 25, dueStage: 'Upon order confirmation' },
  { milestoneName: 'Mid-stage',  percentage: 50, dueStage: 'Midway through work' },
  { milestoneName: 'Completion', percentage: 25, dueStage: 'Upon project completion' },
];

// ─── Terms & conditions ────────────────────────────────────────────────────────
// All arrays are free-form strings — populated from AppPreferences.default_terms.
// Nothing here is interior-specific.

export interface TermsAndConditions {
  materialsUsed: string[];
  inclusions: string[];
  exclusions: string[];
  generalTerms: string[];
  warranty: string[];
  customTerms?: string;
  termsVersion?: string;
}

// ─── Revision tracking ────────────────────────────────────────────────────────

export interface RevisionLogEntry {
  revisionNumber: number;
  date: string;
  changedBy: string;
  whatChanged: string;
}

// ─── Client acceptance ────────────────────────────────────────────────────────

export interface ClientAcceptance {
  clientName: string;
  acceptanceDate?: string;
  signatureDataUrl?: string;
  termsAccepted: boolean;
  companySignatoryName?: string;
  companySignatureUrl?: string;
}

// ─── Catalogue types (re-exported for convenience) ────────────────────────────

export interface Brand {
  id: string;
  name: string;
  logoUrl?: string;
  isRecommended: boolean;
}

export interface CatalogItem {
  id: string;
  category: string;         // open — from catalogue
  name: string;
  description: string;
  specifications: string;
  defaultUom: UnitOfMeasure;
  tags?: string[]; // hierarchical tags (e.g. ['modular', 'wardrobe', 'hardware'])
  hasDimensions: boolean;
  pricingRules?: PricingRules;
}

export interface BrandPricing {
  id: string;
  itemId: string;
  brandId: string;
  materialGrade?: string;
  rate: number;
  descriptionOverride?: string;
  specsOverride?: string;
  isRecommended: boolean;
}

export interface GlobalSettings {
  id: string;
  key: string;
  value: unknown;
  description?: string;
}

// ─── Metadata ─────────────────────────────────────────────────────────────────

export interface QuotationMetadata {
  quoteId: string;
  quoteNumber: string;
  fileNumber: string;
  estimateType: EstimateType;
  version: number;
  status: QuoteStatus;
  locked: boolean;
  issuedOn: string;
  validityDays: number;
  executionDays?: number;
  reference?: string;
  bdmId?: string;
  notes?: string;
  industryId?: string;
  branding_snapshot?: any;
  isTemplate?: boolean;
  templateName?: string;
  // ── Lineage fields (populated from DB, never written by frontend) ──
  groupId?: string;          // UUID: all versions of this quote share the same groupId
  parentId?: string;         // UUID: the version this was revised from (null for originals)
  revisionType?: RevisionType;
  isLatestVersion?: boolean;
  supersededBy?: string;     // UUID: the version that replaced this one
  // ── Audit timestamps (set by DB RPCs) ──
  finalizedAt?: string;
  finalizedBy?: string;
  supersededAt?: string;
  cancelledAt?: string;
  cancelledBy?: string;
  cancellationReason?: string;
}

export interface SystemMetadata {
  createdAt: string;
  updatedAt: string;
  createdBy: string;
  updatedBy: string;
  templateVersion: string;
}

// ─── Root Quotation ───────────────────────────────────────────────────────────

export interface Quotation {
  title?: string;
  client_name?: string;
  project_name?: string;
  hydrationState?: 'summary' | 'full';
  metadata: QuotationMetadata;
  company: CompanyInfo;
  client: ClientInfo;
  rooms: RoomInstance[];
  sections: QuotationSection[];
  financials: Financials;
  proposalNarrative?: string;
  paymentSchedule: PaymentMilestone[];
  terms: TermsAndConditions;
  revisionHistory: RevisionLogEntry[];
  acceptance?: ClientAcceptance;
  system: SystemMetadata;
}

// ─── Defaults ─────────────────────────────────────────────────────────────────
// Minimal, non-industry-specific defaults.
// Real values come from AppPreferences stored in global_settings.

export const DEFAULT_COMPANY: CompanyInfo = {
  brandName: '',
  legalName: '',
  gstin: '',
  address: { line1: '', city: '', state: '', country: 'India', pincode: '' },
  contact: { bdmId: '', bdmName: '', phone: '', email: '' },
  authorizedSignatory: '',
};

export const DEFAULT_TERMS: TermsAndConditions = {
  materialsUsed: [],
  inclusions: [],
  exclusions: [],
  generalTerms: [],
  warranty: [],
};

// ESTIMATE_TYPE_OPTIONS and ROOM_CATEGORY_OPTIONS are intentionally removed.
// Build them at runtime from AppPreferences.estimate_types and
// AppPreferences.scope_group_types in the relevant UI components.
// This makes the app domain-agnostic — no rebuild needed to change these lists.
